import { useEffect, useRef } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { useTenantSettings, useUpdateTenantSettings, useUploadTenantLogo } from '../../hooks';
import {
  emptyBusinessInformation,
  emptyContactInformation,
  toSettingsUpdate,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
} from '../../lib';
import type {
  BankAccount,
  BusinessInformation,
  ContactInformation,
  LegalEntityInformation,
  SalesMode,
  TenantSettings,
  UpdateTenantSettingsRequest,
} from '../../model/settingsTypes';
import { CURRENCY_OPTIONS, LANGUAGE_OPTIONS, SALES_MODE_HELP, SALES_MODE_OPTIONS, TIMEZONE_OPTIONS } from '../options';
import {
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import { FeedbackSnackbar } from '../components/FeedbackSnackbar';

// Same owner code the Telegram Mini App storefront upload used before this card moved here —
// kept unchanged in the move (design-channel-connection-pages.md §3.1/Q3): a Telegram-shaped
// owner for a tenant-wide logo, out of scope for this plan to re-scope.
const TELEGRAM_SALES_CHANNEL_CODE = 'TELEGRAM_MINI_APP_SHOP';

function emptyLegalEntity(): LegalEntityInformation {
  return {
    legalForm: '',
    legalName: '',
    inn: '',
    kpp: '',
    ogrn: '',
    okved: '',
    taxSystem: '',
    legalAddress: '',
    actualAddress: '',
    directorName: '',
    chiefAccountant: '',
    authorizedSignerName: '',
    vatNote: '',
  };
}

function buildTenantSettingsUpdate(
  draft: TenantSettings,
  currentLogoUrl: string | null | undefined,
): UpdateTenantSettingsRequest {
  const update = toSettingsUpdate(draft);
  if (update.businessInformation) {
    const requestedLogoUrl = update.businessInformation.logoUrl?.trim();
    update.businessInformation = {
      ...update.businessInformation,
      logoUrl: requestedLogoUrl ? requestedLogoUrl : (currentLogoUrl ?? null),
    };
  }
  return update;
}

// ── Storefront cards (moved from TelegramSettingsPage — plan-conversation-001 WP-MP.2) ──────────
// design-channel-connection-pages.md §4: the same `TenantSettings` resource the rest of this page
// already edits, so these four cards join the page's single save bar — no new API, no second
// resource. `settings.json` keys renamed `telegramSettings.*` → `tenantSettings.storefront*`
// (values kept, not re-translated).

function CompanyLogoField({
  value,
  disabled,
  uploading,
  onPickFile,
}: {
  value: string | null | undefined;
  disabled: boolean;
  uploading: boolean;
  onPickFile: (file: File) => void;
}) {
  const { t } = useTranslation('settings');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    onPickFile(file);
  };

  return (
    <Box>
      <Stack spacing={1.5}>
        {value ? (
          <Box
            component="img"
            src={value}
            alt={t('tenantSettings.storefrontLogoField.altText')}
            sx={{
              maxWidth: 240,
              maxHeight: 96,
              width: 'fit-content',
              objectFit: 'contain',
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              p: 1,
            }}
          />
        ) : (
          <Alert severity="info" variant="outlined">
            {t('tenantSettings.storefrontLogoField.noLogo')}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button
            type="button"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
          >
            {value
              ? t('tenantSettings.storefrontLogoField.replaceButton')
              : t('tenantSettings.storefrontLogoField.uploadButton')}
          </Button>
          {value ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
          ) : null}
        </Stack>

        {/* aria-hidden + tabIndex ensure the input is fully absent from the accessibility tree. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </Stack>
    </Box>
  );
}

function StorefrontProfileSection({
  value,
  contactValue,
  onChange,
  onContactChange,
  disabled,
}: {
  value: BusinessInformation;
  contactValue: ContactInformation;
  onChange: (value: BusinessInformation) => void;
  onContactChange: (value: ContactInformation) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <SettingsCard
      title={t('tenantSettings.storefrontProfileSection.title')}
      description={t('tenantSettings.storefrontProfileSection.description')}
      icon={<LanguageRoundedIcon />}
    >
      <TextInputField
        label={t('tenantSettings.storefrontProfileSection.storeNameLabel')}
        value={value.storeName ?? ''}
        onChange={(next) => onChange({ ...value, storeName: next })}
        disabled={disabled}
      />
      <SelectField
        label={t('tenantSettings.storefrontProfileSection.timeZoneLabel')}
        value={value.timezone ?? ''}
        options={TIMEZONE_OPTIONS}
        onChange={(next) => onChange({ ...value, timezone: next })}
        disabled={disabled}
      />
      <SelectField
        label={t('tenantSettings.storefrontProfileSection.currencyLabel')}
        value={value.currency ?? ''}
        options={CURRENCY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
        onChange={(next) => onChange({ ...value, currency: next })}
        disabled={disabled}
      />
      <SelectField
        label={t('tenantSettings.storefrontProfileSection.localeLabel')}
        description={t('tenantSettings.storefrontProfileSection.localeDescription')}
        value={value.locale ?? ''}
        options={LANGUAGE_OPTIONS}
        onChange={(next) => onChange({ ...value, locale: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.storefrontProfileSection.telegramLabel')}
        description={t('tenantSettings.storefrontProfileSection.telegramDescription')}
        value={contactValue.telegram ?? ''}
        onChange={(next) => onContactChange({ ...contactValue, telegram: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.storefrontProfileSection.websiteLabel')}
        description={t('tenantSettings.storefrontProfileSection.websiteDescription')}
        value={contactValue.website ?? ''}
        onChange={(next) => onContactChange({ ...contactValue, website: next })}
        disabled={disabled}
        type="url"
      />
    </SettingsCard>
  );
}

function ContactInformationSection({
  value,
  onChange,
  disabled,
}: {
  value: ContactInformation;
  onChange: (value: ContactInformation) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <SettingsCard
      title={t('tenantSettings.contactsSection.title')}
      description={t('tenantSettings.contactsSection.description')}
      icon={<ContactsRoundedIcon />}
    >
      <TextInputField
        label={t('tenantSettings.contactsSection.phoneLabel')}
        value={value.phone ?? ''}
        onChange={(next) => onChange({ ...value, phone: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.contactsSection.emailLabel')}
        value={value.email ?? ''}
        onChange={(next) => onChange({ ...value, email: next })}
        disabled={disabled}
        type="email"
      />
    </SettingsCard>
  );
}

function LegalEntitySection({
  value,
  onChange,
  disabled,
}: {
  value: LegalEntityInformation;
  onChange: (value: LegalEntityInformation) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  return (
    <SettingsCard
      title={t('tenantSettings.legalSection.title')}
      description={t('tenantSettings.legalSection.description')}
      icon={<DomainRoundedIcon />}
    >
      <TextInputField
        label={t('tenantSettings.legalSection.legalFormLabel')}
        value={value.legalForm ?? ''}
        onChange={(next) => onChange({ ...value, legalForm: next })}
        disabled={disabled}
        placeholder={t('tenantSettings.legalSection.legalFormPlaceholder')}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.legalNameLabel')}
        value={value.legalName ?? ''}
        onChange={(next) => onChange({ ...value, legalName: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.innLabel')}
        value={value.inn ?? ''}
        onChange={(next) => onChange({ ...value, inn: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.kppLabel')}
        value={value.kpp ?? ''}
        onChange={(next) => onChange({ ...value, kpp: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.ogrnLabel')}
        value={value.ogrn ?? ''}
        onChange={(next) => onChange({ ...value, ogrn: next })}
        disabled={disabled}
      />
      {/* Special case (plan-009): the description keeps the Russian legal/tax term visible
          parenthetically — real Russian legal terminology a merchant needs to recognize
          regardless of UI language, not a translation gap (i18n-compliance-audit.md). */}
      <TextInputField
        label={t('tenantSettings.legalSection.okvedLabel')}
        description={t('tenantSettings.legalSection.okvedDescription')}
        value={value.okved ?? ''}
        onChange={(next) => onChange({ ...value, okved: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.taxSystemLabel')}
        description={t('tenantSettings.legalSection.taxSystemDescription')}
        value={value.taxSystem ?? ''}
        onChange={(next) => onChange({ ...value, taxSystem: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.legalAddressLabel')}
        value={value.legalAddress ?? ''}
        onChange={(next) => onChange({ ...value, legalAddress: next })}
        disabled={disabled}
        multiline
        rows={2}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.actualAddressLabel')}
        value={value.actualAddress ?? ''}
        onChange={(next) => onChange({ ...value, actualAddress: next })}
        disabled={disabled}
        multiline
        rows={2}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.directorNameLabel')}
        value={value.directorName ?? ''}
        onChange={(next) => onChange({ ...value, directorName: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.chiefAccountantLabel')}
        description={t('tenantSettings.legalSection.chiefAccountantDescription')}
        value={value.chiefAccountant ?? ''}
        onChange={(next) => onChange({ ...value, chiefAccountant: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.authorizedSignerLabel')}
        description={t('tenantSettings.legalSection.authorizedSignerDescription')}
        value={value.authorizedSignerName ?? ''}
        onChange={(next) => onChange({ ...value, authorizedSignerName: next })}
        disabled={disabled}
      />
      <TextInputField
        label={t('tenantSettings.legalSection.vatNoteLabel')}
        description={t('tenantSettings.legalSection.vatNoteDescription')}
        value={value.vatNote ?? ''}
        onChange={(next) => onChange({ ...value, vatNote: next })}
        disabled={disabled}
      />
    </SettingsCard>
  );
}

function BankAccountsSection({
  value,
  onChange,
  disabled,
}: {
  value: BankAccount[];
  onChange: (value: BankAccount[]) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  const updateAccount = (index: number, patch: Partial<BankAccount>) => {
    onChange(
      value.map((account, currentIndex) =>
        currentIndex === index ? { ...account, ...patch } : account,
      ),
    );
  };

  const removeAccount = (index: number) => {
    const next = value.filter((_, currentIndex) => currentIndex !== index);
    if (next.length > 0 && !next.some((account) => account.defaultAccount)) {
      next[0] = { ...next[0], defaultAccount: true };
    }
    onChange(next);
  };

  const addAccount = () => {
    onChange([
      ...value,
      {
        id: null,
        label: '',
        bankName: '',
        bik: '',
        correspondentAccount: '',
        accountNumber: '',
        currency: '',
        beneficiaryName: '',
        defaultAccount: value.length === 0,
        position: value.length,
      },
    ]);
  };

  const setDefaultAccount = (index: number, checked: boolean) => {
    const next = value.map((account, currentIndex) =>
      checked
        ? { ...account, defaultAccount: currentIndex === index }
        : currentIndex === index
          ? { ...account, defaultAccount: false }
          : account,
    );
    // If unchecking left no default, auto-promote the first account so the UI
    // always reflects what the server will do on save (fallback to index 0).
    if (!checked && !next.some((a) => a.defaultAccount) && next.length > 0) {
      next[0] = { ...next[0], defaultAccount: true };
    }
    onChange(next);
  };

  return (
    <SettingsCard
      title={t('tenantSettings.bankAccountsSection.title')}
      description={t('tenantSettings.bankAccountsSection.description')}
      icon={<AccountBalanceRoundedIcon />}
    >
      <Alert severity="info" variant="outlined">
        {t('tenantSettings.bankAccountsSection.listNotice')}
      </Alert>
      {value.length === 0 ? (
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('tenantSettings.bankAccountsSection.emptyMessage')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          {value.map((account, index) => (
            <SettingsCard
              key={account.id ?? `${account.accountNumber ?? 'new'}-${index}`}
              title={
                account.label?.trim() ||
                t('tenantSettings.bankAccountsSection.accountTitleFallback', { index: index + 1 })
              }
              description={
                account.accountNumber?.trim()
                  ? t('tenantSettings.bankAccountsSection.accountNumberDescription', {
                      number: account.accountNumber.trim(),
                    })
                  : t('tenantSettings.bankAccountsSection.fillDetailsDescription')
              }
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('tenantSettings.bankAccountsSection.defaultLabel')}
                  </Typography>
                  <Switch
                    checked={account.defaultAccount}
                    disabled={disabled}
                    onChange={(event) => setDefaultAccount(index, event.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': t('tenantSettings.bankAccountsSection.defaultAria', {
                          index: index + 1,
                        }),
                      },
                    }}
                  />
                  <IconButton
                    aria-label={t('tenantSettings.bankAccountsSection.removeAria', {
                      index: index + 1,
                    })}
                    onClick={() => removeAccount(index)}
                    disabled={disabled}
                    color="error"
                    size="small"
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              }
            >
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.labelFieldLabel')}
                description={t('tenantSettings.bankAccountsSection.labelFieldDescription')}
                value={account.label ?? ''}
                onChange={(next) => updateAccount(index, { label: next })}
                disabled={disabled}
              />
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.bankNameLabel')}
                value={account.bankName ?? ''}
                onChange={(next) => updateAccount(index, { bankName: next })}
                disabled={disabled}
              />
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.bikLabel')}
                value={account.bik ?? ''}
                onChange={(next) => updateAccount(index, { bik: next })}
                disabled={disabled}
              />
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.correspondentAccountLabel')}
                value={account.correspondentAccount ?? ''}
                onChange={(next) => updateAccount(index, { correspondentAccount: next })}
                disabled={disabled}
              />
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.accountNumberLabel')}
                value={account.accountNumber ?? ''}
                onChange={(next) => updateAccount(index, { accountNumber: next })}
                disabled={disabled}
              />
              <SelectField
                label={t('tenantSettings.bankAccountsSection.currencyLabel')}
                value={account.currency ?? ''}
                options={CURRENCY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(next) => updateAccount(index, { currency: next })}
                disabled={disabled}
                allowEmpty
              />
              <TextInputField
                label={t('tenantSettings.bankAccountsSection.beneficiaryNameLabel')}
                value={account.beneficiaryName ?? ''}
                onChange={(next) => updateAccount(index, { beneficiaryName: next })}
                disabled={disabled}
              />
              <NumberField
                label={t('tenantSettings.bankAccountsSection.positionLabel')}
                description={t('tenantSettings.bankAccountsSection.positionDescription')}
                value={account.position}
                onChange={(next) => updateAccount(index, { position: next })}
                disabled={disabled}
                min={0}
              />
            </SettingsCard>
          ))}
        </Stack>
      )}
      <Box sx={{ pt: 1 }}>
        <Button
          startIcon={<AddRoundedIcon />}
          onClick={addAccount}
          disabled={disabled}
          size="small"
        >
          {t('tenantSettings.bankAccountsSection.addAccount')}
        </Button>
      </Box>
    </SettingsCard>
  );
}

/**
 * Company profile settings: storefront profile/logo/status/capabilities (moved from the Telegram
 * page — plan-conversation-001 WP-MP.2), contacts, legal entity and bank accounts.
 */
export function TenantSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('tenant');
  const query = useTenantSettings();
  const update = useUpdateTenantSettings();
  const form = useSettingsForm(query.data);
  const uploadLogo = useUploadTenantLogo();
  const { feedback, propagation, success, error: notifyError, clear } = useFeedback();

  // Keep a live ref to the draft so the async logo-upload onSuccess callback always reads the
  // latest draft state, not the stale closure captured when the upload started.
  const formDraftRef = useRef(form.draft);
  useEffect(() => {
    formDraftRef.current = form.draft;
  }, [form.draft]);

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(buildTenantSettingsUpdate(form.draft, query.data?.businessInformation?.logoUrl), {
      onSuccess: () => propagation(t('tenantSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  const handleLogoUpload = (file: File) => {
    uploadLogo.mutate(
      { file, ownerId: TELEGRAM_SALES_CHANNEL_CODE },
      {
        onSuccess: (asset) => {
          form.setField('businessInformation', {
            ...(formDraftRef.current?.businessInformation ?? emptyBusinessInformation()),
            logoUrl: asset.publicUrl,
          });
          success(t('tenantSettings.storefrontLogoUploadedFeedback'));
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  return (
    <SettingsScaffold
      title={t('tenantSettings.title')}
      subtitle={t('tenantSettings.subtitle')}
      icon={<DomainRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        const businessInformation = draft.businessInformation ?? emptyBusinessInformation();
        const contactInformation = draft.contactInformation ?? emptyContactInformation();
        const legalEntity = draft.legalEntityInformation ?? emptyLegalEntity();
        const bankAccounts = draft.bankAccounts ?? [];

        return (
          <Stack spacing={2.5}>
            <StorefrontProfileSection
              value={businessInformation}
              contactValue={contactInformation}
              onChange={(value) => form.setField('businessInformation', value)}
              onContactChange={(value) => form.setField('contactInformation', value)}
              disabled={disabled}
            />

            <SettingsCard
              title={t('tenantSettings.storefrontLogoSection.title')}
              description={t('tenantSettings.storefrontLogoSection.description')}
              icon={<CloudUploadRoundedIcon />}
            >
              <CompanyLogoField
                value={businessInformation.logoUrl}
                disabled={disabled}
                uploading={uploadLogo.isPending}
                onPickFile={handleLogoUpload}
              />
            </SettingsCard>

            <SettingsCard
              title={t('tenantSettings.storefrontStatusSection.title')}
              description={t('tenantSettings.storefrontStatusSection.description')}
              icon={<StorefrontRoundedIcon />}
            >
              <SwitchField
                label={t('tenantSettings.storefrontStatusSection.enabledLabel')}
                description={t('tenantSettings.storefrontStatusSection.enabledDescription')}
                checked={draft.storefrontEnabled ?? false}
                onChange={(value) => form.setField('storefrontEnabled', value)}
                disabled={disabled}
              />
              <SelectField
                label={t('tenantSettings.storefrontStatusSection.salesModeLabel')}
                description={t(SALES_MODE_HELP[draft.salesMode ?? 'PRE_OPENING'])}
                value={draft.salesMode ?? 'PRE_OPENING'}
                options={SALES_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('salesMode', value as SalesMode)}
                disabled={disabled}
              />
              <Box sx={{ pt: 1 }}>
                <Alert severity="info" variant="outlined">
                  <Trans
                    t={t}
                    i18nKey="tenantSettings.storefrontStatusSection.salesModeNotice"
                    components={{ strong: <strong /> }}
                  >
                    Sales mode gates the storefront. In <strong>PRE_OPENING</strong> and{' '}
                    <strong>PAUSED</strong>, purchasing is restricted. In{' '}
                    <strong>MAINTENANCE</strong>, the storefront is unavailable.
                  </Trans>
                </Alert>
              </Box>
            </SettingsCard>

            <SettingsCard
              title={t('tenantSettings.storefrontCapabilitiesSection.title')}
              description={t('tenantSettings.storefrontCapabilitiesSection.description')}
              icon={<ShoppingCartCheckoutRoundedIcon />}
            >
              <SwitchField
                label={t('tenantSettings.storefrontCapabilitiesSection.catalogLabel')}
                description={t('tenantSettings.storefrontCapabilitiesSection.catalogDescription')}
                checked={draft.catalogEnabled ?? false}
                onChange={(value) => form.setField('catalogEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('tenantSettings.storefrontCapabilitiesSection.basketLabel')}
                description={t('tenantSettings.storefrontCapabilitiesSection.basketDescription')}
                checked={draft.basketEnabled ?? false}
                onChange={(value) => form.setField('basketEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('tenantSettings.storefrontCapabilitiesSection.checkoutLabel')}
                description={t('tenantSettings.storefrontCapabilitiesSection.checkoutDescription')}
                checked={draft.checkoutEnabled ?? false}
                onChange={(value) => form.setField('checkoutEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('tenantSettings.storefrontCapabilitiesSection.ordersLabel')}
                description={t('tenantSettings.storefrontCapabilitiesSection.ordersDescription')}
                checked={draft.ordersEnabled ?? false}
                onChange={(value) => form.setField('ordersEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('tenantSettings.storefrontCapabilitiesSection.paymentLabel')}
                description={t('tenantSettings.storefrontCapabilitiesSection.paymentDescription')}
                checked={draft.paymentEnabled ?? false}
                onChange={(value) => form.setField('paymentEnabled', value)}
                disabled={disabled}
              />
              <Box sx={{ pt: 1 }}>
                <Alert severity="info" variant="outlined">
                  <Trans
                    t={t}
                    i18nKey="tenantSettings.storefrontCapabilitiesSection.gatingNotice"
                    components={{ strong: <strong /> }}
                  >
                    Capabilities are also gated by the sales mode — ordering and payment stay off
                    until the storefront is <strong>ACTIVE</strong>, regardless of these toggles.
                  </Trans>
                </Alert>
              </Box>
            </SettingsCard>

            <ContactInformationSection
              value={contactInformation}
              onChange={(value) => form.setField('contactInformation', value)}
              disabled={disabled}
            />

            <LegalEntitySection
              value={legalEntity}
              onChange={(value) => form.setField('legalEntityInformation', value)}
              disabled={disabled}
            />

            <BankAccountsSection
              value={bankAccounts}
              onChange={(value) => form.setField('bankAccounts', value)}
              disabled={disabled}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
